import { Alert, Button, Stack, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import biApi from "../../../../../../../api/biApi";
import type { ClientInfoExtended, ReportTemplate } from "../../../../../../../api/types/svTypes.ts";
import DataSource from "../../../../report-lists/templates/DataSources.tsx";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import { updateReport } from "../../../store/thunks/currentReportThunks";

type UpdateDataSourceModel = Pick<ReportTemplate, "dataSource">;
interface Props {
  template: ReportTemplate;
  companies: ClientInfoExtended[];
  onClose: () => void;
  onSaved: (dataSource: string, dataSourceChanged?: boolean) => void;
}

export default function DataSourceDialog({ template, onClose, onSaved, companies }: Props) {
  const { palette } = useTheme();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [newDataSource, setNewDataSource] = useState<UpdateDataSourceModel>(() => ({
    dataSource:
      companies.find((c) => c.clientCode === template.dataSource)?.clientCode || companies[0]?.clientCode || "",
  }));

  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await biApi.updateReportTemplateDataSource({
        dataSource: newDataSource.dataSource,
        templateId: template.reportId,
      });
      if (response.success && response.data) {
        dispatch(updateReport({ ...response.data }));
        sendNotification("The template was successfully saved.");
        onSaved(response.data.dataSource);
      } else {
        setError("An error occurred while saving the template.");
        sendNotificationError("An error occurred while saving the template.");
      }
    } catch {
      setError("An error occurred while saving the template.");
      sendNotificationError("An error occurred while saving the template.");
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [newDataSource, onSaved, template.reportId]);

  return (
    <PromptDialog open={true} sx={{ width: 517, minHeight: 284, p: 0 }}>
      <PromptDialogHeader text="Change Data Source" disabled={isSaving} onClose={onClose} />
      {error && (
        <Alert sx={{ mb: ".5rem" }} severity="error">
          An error occurred while changing the data source.
        </Alert>
      )}
      <Stack sx={{ px: 3, py: 2, flex: 1 }}>
        <DataSource
          title="Data Source"
          disclaimer="The data provided by this company will be used to demonstrate the report template."
          dataSource={newDataSource.dataSource}
          companies={companies}
          onChange={(company) => setNewDataSource((prev) => ({ ...prev, dataSource: company }))}
          sx={{ " #disclaimer": { color: palette.text.secondary } }}
        />
      </Stack>
      <DialogButtons onCancel={onClose} cancelDisabled={isSaving}>
        <Button variant="contained" disabled={isSaving} loading={isSaving} onClick={onSave}>
          Save
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
